import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Card,
    CardContent,
    IconButton,
    Avatar,
    Chip,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputAdornment,
    CircularProgress,
    Backdrop,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Alert,
    AlertTitle,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Email as EmailIcon,
    AccessTime as TimeIcon,
    Send as SendIcon,
    Close as CloseIcon,
    Warning as WarningIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import api from '../../api/api';
import CustomSnackbar from '../../components/CustomSnackbar';
import DownloadIcon from "@mui/icons-material/Download";

import translationsMap from '../../en-ja-map';
import { DownloadCSV } from '../../Reusable Components/DownloadCSV';
const t = (k) => translationsMap?.[k] ?? k;

// Validation utilities
const ValidationUtils = {
    dosePattern: /^(\d+(\.\d+)?)\s*(mg|g|ml|mcg|units?|iu|mEq)$/i,
    validateDose: (dose) => {
        if (!dose?.trim()) return { isValid: false, error: t('Dose is required') };
        if (!ValidationUtils.dosePattern.test(dose.trim())) {
            return {
                isValid: false,
                error: t('Invalid dose format. Use format like "5mg", "2.5ml", "10units"')
            };
        }
        const numericValue = parseFloat(dose);
        if (numericValue <= 0) {
            return { isValid: false, error: t('Dose must be greater than 0') };
        }
        if (numericValue > 10000) {
            return { isValid: false, error: t('Dose seems unusually high. Please verify') };
        }
        return { isValid: true, error: null };
    },

    validateMedicineName: (name) => {
        if (!name?.trim()) return { isValid: false, error: t('Medicine name is required') };
        if (name.trim().length < 2) {
            return { isValid: false, error: t('Medicine name too short') };
        }
        if (name.trim().length > 100) {
            return { isValid: false, error: t('Medicine name too long') };
        }
        if (/^\d+$/.test(name.trim())) {
            return { isValid: false, error: t('Medicine name cannot be only numbers') };
        }
        return { isValid: true, error: null };
    },

    checkDrugInteractions: (medications) => {
        const interactions = [];
        const drugNames = medications.map(med => med.name.toLowerCase());

        const knownInteractions = [
            { drugs: ['warfarin', 'aspirin'], warning: t('Increased bleeding risk') },
            { drugs: ['metformin', 'alcohol'], warning: t('Risk of lactic acidosis') },
            { drugs: ['lisinopril', 'potassium'], warning: t('Risk of hyperkalemia') },
        ];

        knownInteractions.forEach(interaction => {
            const foundDrugs = interaction.drugs.filter(drug =>
                drugNames.some(medName => medName.includes(drug))
            );
            if (foundDrugs.length >= 2) {
                interactions.push({
                    drugs: foundDrugs,
                    warning: interaction.warning
                });
            }
        });

        return interactions;
    }
};

const PrescriptionManagement = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { state } = location;

    const isNewPrescriptionMode = state && state.appointment && state.appointment.id;
    const isHistoryOnlyMode = state && state.patientId && !state.appointment;

    const appointmentData = state?.appointment;
    const initialPatientId = appointmentData?.user_id || state?.patientId;
    const patientData = appointmentData?.user;

    const [selectedPatientId, setSelectedPatientId] = useState(initialPatientId);
    const [activeTab, setActiveTab] = useState(isNewPrescriptionMode ? 'new' : 'history');

    // Loading states
    const [loading, setLoading] = useState(false);
    const [pharmaciesLoading, setPharmaciesLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [patientLoading, setPatientLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [packages, setPackages] = useState([]);

    // Validation states
    const [validationErrors, setValidationErrors] = useState({});
    const [drugInteractions, setDrugInteractions] = useState([]);
    const [touchedFields, setTouchedFields] = useState({});

    // Dialog states
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        type: 'success'
    });

    const [prescriptionHistory, setPrescriptionHistory] = useState([]);
    const [showPatientDialog, setShowPatientDialog] = useState(!initialPatientId);
    const [pharmacies, setPharmacies] = useState([]);
    const [patientAddress, setPatientAddress] = useState('');
    const [medications, setMedications] = useState([
        { id: Date.now(), name: '', dose: '', frequency: '', duration: '', units: '' }
    ]);
    const [selectedPackage, setSelectedPackage] = useState('');
    const [selectedPharmacy, setSelectedPharmacy] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [patients, setPatients] = useState([]);
    const [selectedPatientData, setSelectedPatientData] = useState(null);
    const user = JSON.parse(localStorage.getItem('user'));
    const [showSelectPatientMessage, setShowSelectPatientMessage] = useState(false);

    useEffect(() => {
        api.get("v1/clinic/get/prescription/packages")
            .then((res) => {
                console.log(res.data.data, "prescription packages");
                setPackages(res.data.data || []);
            })
            .catch((err) => {
                console.error("Error fetching prescription packages:", err);
            })
    }, []);

    const selectedPatient = selectedPatientData || (appointmentData ? {
        id: patientData?.id || selectedPatientId,
        name: patientData?.name || appointmentData.clinic_checkin_data?.full_name || t('Unknown Patient'),
        age: appointmentData.clinic_checkin_data?.age || 'N/A',
        medicalId: `${t('M.ID:')} ${patientData?.id || selectedPatientId}`,
        phone: patientData?.phone_number || 'N/A',
        email: patientData?.email || appointmentData.clinic_checkin_data?.email || 'N/A',
        bmi: appointmentData.clinic_checkin_data?.bmi || 'N/A',
        height: appointmentData.clinic_checkin_data?.height || 'N/A',
        weight: appointmentData.clinic_checkin_data?.weight || 'N/A',
        sex: appointmentData.clinic_checkin_data?.sex || 'N/A',
        symptoms: appointmentData.clinic_checkin_data?.symptoms || t('No symptoms recorded'),
        address: patientAddress || t('Loading address...')
    } : {
        id: selectedPatientId || t('Unknown'),
        name: t('Unknown Patient'),
        age: 'N/A',
        medicalId: `${t('M.ID:')} ${selectedPatientId || t('Unknown')}`,
        phone: 'N/A',
        email: 'N/A',
        address: patientAddress || t('Loading address...')
    });

    const medicineOptions = [t('Wegovy')];

    const getDurationOptions = (frequency, isFGPackage) => {
        if (isFGPackage) {
            return [`3 ${t('months')}`];
        }

        if (!frequency) return [];

        if (frequency === t('Weekly')) {
            return Array.from({ length: 12 }, (_, i) => `${i + 1} ${t('months')}`);
        }

        return Array.from({ length: 30 }, (_, i) => `${i + 1} ${t('days')}`);
    };

    const getFrequencyMultiplier = (frequency) => {
        switch (frequency) {
            case t('Once daily'):
                return 1;
            case t('Twice daily'):
                return 2;
            case t('Three times daily'):
                return 3;
            case t('Weekly'):
                return 1;
            default:
                return 1;
        }
    };

    const getUnitsStep = (frequency) => {
        switch (frequency) {
            case t('Once daily'):
                return 1;
            case t('Twice daily'):
                return 2;
            case t('Three times daily'):
                return 3;
            case t('Weekly'):
                return 4;
            default:
                return 1;
        }
    };

    const getUnitsMax = (frequency) => {
        if (frequency === t('Weekly')) return 48;
        const step = getUnitsStep(frequency);
        return 30 * step;
    };

    const getUnitsOptions = (frequency) => {
        if (!frequency) return [];
        const step = getUnitsStep(frequency);
        const max = getUnitsMax(frequency);
        const count = Math.floor(max / step);
        return Array.from({ length: count }, (_, i) => (i + 1) * step);
    };

    const calculateUnits = (med) => {
        if (!med.frequency || !med.duration) return '';

        const multiplier = getFrequencyMultiplier(med.frequency);
        const durationStr = med.duration || '';

        const durationValue = parseInt(durationStr) || 0;
        if (!durationValue) return '';

        if (med.frequency === t('Weekly')) {

            if (durationStr.includes(t('months'))) {
                return durationValue * 4;
            }
            if (durationStr.includes(t('days'))) {
                return Math.ceil(durationValue / 7);
            }
            return durationValue * 4;
        }

        return durationValue * multiplier;
    };

    const calculateDuration = (frequency, units) => {
        if (!frequency || !units) return '';

        const unitsNum = parseInt(units, 10);

        if (!unitsNum || unitsNum <= 0) return '';

        // Weekly
        if (frequency === t('Weekly')) {
            const months = unitsNum / 4;

            if (Number.isInteger(months) && months > 0) {
                return `${months} ${t('months')}`;
            }

            return '';
        }

        // Daily frequencies
        const multiplierMap = {
            [t('Once daily')]: 1,
            [t('Twice daily')]: 2,
            [t('Three times daily')]: 3,
        };

        const multiplier = multiplierMap[frequency];

        if (!multiplier) return '';

        const days = unitsNum / multiplier;

        if (Number.isInteger(days) && days > 0) {
            return `${days} ${t('days')}`;
        }

        return '';
    };

    const calculateFrequencyFromDurationAndUnits = (duration, units) => {
        if (!duration || !units) return '';

        const durationValue = parseInt(duration, 10);
        const unitsNum = parseInt(units, 10);

        if (!durationValue || !unitsNum) return '';

        // Weekly check
        if (
            duration.includes(t('months')) ||
            duration.includes('ヶ月') ||
            duration.includes('month')
        ) {
            if (unitsNum === durationValue * 4) {
                return t('Weekly');
            }

            return '';
        }

        // Daily checks
        const onceDaily = durationValue * 1;
        const twiceDaily = durationValue * 2;
        const threeTimesDaily = durationValue * 3;

        if (unitsNum === onceDaily) {
            return t('Once daily');
        }

        if (unitsNum === twiceDaily) {
            return t('Twice daily');
        }

        if (unitsNum === threeTimesDaily) {
            return t('Three times daily');
        }

        return '';
    };

    const updateMedication = (id, field, value) => {
        setMedications((prev) =>
            prev.map((med) => {
                if (med.id !== id) return med;

                // Existing FG package logic
                if (med.isFGPackage) {
                    if (field === 'duration') {
                        return med;
                    }

                    if (field === 'units') {
                        return { ...med, units: value };
                    }

                    if (field === 'frequency') {
                        return {
                            ...med,
                            frequency: value,
                            duration: `3 ${t('months')}`,
                            units: getUnitsStep(value),
                            isDurationDisabled: true,
                        };
                    }

                    if (field === 'name' || field === 'dose') {
                        return {
                            ...med,
                            [field]: value,
                            duration: `3 ${t('months')}`,
                            isDurationDisabled: true,
                        };
                    }

                    return med;
                }

                const updatedMed = {
                    ...med,
                    [field]: value,
                };

                // Frequency selected
                if (field === 'frequency') {
                    updatedMed.duration = '';
                    updatedMed.units = getUnitsStep(value);
                }

                // Frequency + Duration => Auto Units
                if (
                    updatedMed.frequency &&
                    updatedMed.duration &&
                    (field === 'frequency' || field === 'duration')
                ) {
                    updatedMed.units = calculateUnits(updatedMed);
                }

                // Frequency + Units => Auto Duration
                if (
                    updatedMed.frequency &&
                    updatedMed.units &&
                    field === 'units'
                ) {
                    const computedDuration = calculateDuration(
                        updatedMed.frequency,
                        updatedMed.units
                    );

                    if (computedDuration) {
                        updatedMed.duration = computedDuration;
                    }
                }

                // Duration + Units => Auto Frequency
                // Duration + Units => Auto Frequency
                if (
                    updatedMed.duration &&
                    updatedMed.units &&
                    !updatedMed.frequency &&
                    (field === 'duration' || field === 'units')
                ) {
                    const computedFrequency =
                        calculateFrequencyFromDurationAndUnits(
                            updatedMed.duration,
                            updatedMed.units
                        );

                    if (computedFrequency) {
                        updatedMed.frequency = computedFrequency;
                    }
                }

                return updatedMed;
            })
        );

        markFieldAsTouched(id, field);
    };
    const frequencyOptions = [
        t('Once daily'), t('Twice daily'), t('Three times daily'),
        //  t('Four times daily'),
        // t('Every 8 hours'), t('Every 12 hours'), t('As needed'),
        t('Weekly')
    ];

    // const packages = [
    //     { id: 'A', name: t('Package A'), dose: '0.25mg', price: '¥44,800', medicine: 'Wegovy', frequency: t('Weekly'), duration: t('3 months') },
    //     { id: 'B', name: t('Package B'), dose: '0.5mg', price: '¥44,800', medicine: 'Wegovy', frequency: t('Weekly'), duration: t('3 months') },
    //     { id: 'C', name: t('Package C'), dose: '1.0mg', price: '¥74,800', medicine: 'Wegovy', frequency: t('Weekly'), duration: t('3 months') },
    //     { id: 'D', name: t('Package D'), dose: '1.7mg', price: '¥98,800', medicine: 'Wegovy', frequency: t('Weekly'), duration: t('3 months') },
    //     { id: 'E', name: t('Package E'), dose: '2.4mg', price: '¥128,800', medicine: 'Wegovy', frequency: t('Weekly'), duration: t('3 months') },
    //     { id: 'F', name: t('Package F'), dose: '1.7mg', price: '¥266,400', medicine: 'Wegovy', frequency: t('Weekly'), duration: t('3 months') },
    //     { id: 'G', name: t('Package G'), dose: '2.4mg', price: '¥356,400', medicine: 'Wegovy', frequency: t('Weekly'), duration: t('3 months') },
    //     { id: 'CUSTOM', name: t('Custom Package'), dose: '', price: '¥0', medicine: '', frequency: '', duration: '' }
    // ];

    const showConfirmation = (title, message, onConfirm) => {
        setConfirmDialog({
            open: true,
            title,
            message,
            onConfirm
        });
    };

    const closeConfirmation = () => {
        setConfirmDialog(prev => ({
            ...prev,
            open: false,
            onConfirm: null,
        }));
        setTimeout(() => {
            setConfirmDialog({
                open: false,
                title: '',
                message: '',
                onConfirm: null
            });
        }, 300);
    };

    // const validateMedication = (medication) => {
    //     const errors = {};

    //     const nameValidation = ValidationUtils.validateMedicineName(medication.name);
    //     if (!nameValidation.isValid) errors.name = nameValidation.error;

    //     const doseValidation = ValidationUtils.validateDose(medication.dose);
    //     if (!doseValidation.isValid) errors.dose = doseValidation.error;

    //     if (!medication.frequency) errors.frequency = t('Frequency is required');
    //     if (!medication.duration) errors.duration = t('Duration is required');

    //     return errors;
    // };

    // const validateAllMedications = () => {
    //     const errors = {};
    //     let hasErrors = false;

    //     medications.forEach((med, index) => {
    //         const medErrors = validateMedication(med);
    //         if (Object.keys(medErrors).length > 0) {
    //             errors[med.id] = medErrors;
    //             hasErrors = true;
    //         }
    //     });

    //     const interactions = ValidationUtils.checkDrugInteractions(medications);
    //     setDrugInteractions(interactions);

    //     setValidationErrors(errors);
    //     return !hasErrors;
    // };

    const markFieldAsTouched = (medicationId, fieldName) => {
        setTouchedFields(prev => ({
            ...prev,
            [medicationId]: {
                ...prev[medicationId],
                [fieldName]: true
            }
        }));
    };

    useEffect(() => {
        const errors = {};
        medications.forEach((med) => {
            const medErrors = {};
            const fieldsTouched = touchedFields[med.id] || {};

            if (fieldsTouched.name) {
                const nameValidation = ValidationUtils.validateMedicineName(med.name);
                if (!nameValidation.isValid) medErrors.name = nameValidation.error;
            }

            if (fieldsTouched.dose) {
                const doseValidation = ValidationUtils.validateDose(med.dose);
                if (!doseValidation.isValid) medErrors.dose = doseValidation.error;
            }

            if (fieldsTouched.frequency && !med.frequency) {
                medErrors.frequency = t('Frequency is required');
            }

            if (fieldsTouched.duration && !med.duration) {
                medErrors.duration = t('Duration is required');
            }

            if (Object.keys(medErrors).length > 0) {
                errors[med.id] = medErrors;
            }
        });

        setValidationErrors(errors);

        const completeMedications = medications.filter(med =>
            med.name?.trim() && med.dose?.trim() && med.frequency && med.duration
        );
        if (completeMedications.length > 1) {
            const interactions = ValidationUtils.checkDrugInteractions(completeMedications);
            setDrugInteractions(interactions);
        } else {
            setDrugInteractions([]);
        }
    }, [medications, touchedFields]);

    const fetchPatientAddress = async () => {
        if (!selectedPatientId) return;

        setPatientLoading(true);
        try {
            const response = await api.get(`v1/clinic/user/address/${selectedPatientId}`);
            const addressData = response.data?.address;
            let formattedAddress = t('Address not available');
            if (addressData) {
                const addressParts = [
                    addressData.roomNo && `${t('Room')} ${addressData.roomNo}`,
                    addressData.building,
                    addressData.street,
                    addressData.ward,
                    addressData.city,
                    addressData.postal_code
                ].filter(Boolean);
                formattedAddress = addressParts.join(', ') || addressData.address || t('Address not available');
            }
            setPatientAddress(formattedAddress);
        } catch (error) {
            console.error('Error fetching patient address:', error);
            setPatientAddress(t('Address not available'));
            showSnackbar(t('Failed to load patient address'), 'warning');
        } finally {
            setPatientLoading(false);
        }
    };

    const getPackageLetter = (packageId) => {
        const pkg = packages.find(p => p.id === packageId);
        if (pkg) {
            return pkg.package_name.split(' ')[1] || 'A';
        }
        return 'A';
    };

    const fetchPrescriptionHistory = async () => {
        if (!selectedPatientId) {
            showSnackbar(t('Select Patient'), 'info');
            return;
        }

        if (activeTab !== "history") {
            return;
        }

        setHistoryLoading(true);
        console.log('Fetching prescription history for patient ID:', activeTab);
        try {
            const historyEndpoint = isNewPrescriptionMode
                ? `v1/clinic/get/completed-prescriptions/${selectedPatientId}`
                : `v1/clinic/get/user-prescriptions/${selectedPatientId}`;

            const response = await api.get(historyEndpoint);
            const transformedHistory = (response.data.prescriptions || []).map(prescription => {
                const prescriptionPackage = packages.find(p => p.id === prescription.prescription_package_id);
                return {
                    id: prescription.id,
                    patientName: selectedPatient?.name || `${t('Patient ID:')} ${prescription.user_id}`,
                    patientId: prescription.user_id,
                    date: new Date(prescription.prescription_date).toLocaleDateString(),
                    time: new Date(prescription.created_at).toLocaleTimeString(),
                    medicationCount: prescription.medicines.length,
                    medications: prescription.medicines.map(med => ({
                        name: med.name,
                        dose: med.dosage,
                        units: med.units,
                        description: `${med.frequency} ${t('for')} ${med.duration}`
                    })),
                    package: prescriptionPackage ? prescriptionPackage.package_name : `${t('Package')} ${getPackageLetter(prescription.prescription_package_id)}`,

                    // price: `¥${prescription.total_amount.toLocaleString()}`,

                    consultation_fee: `¥${prescription.prescription_package?.consultation_fee.toLocaleString()}`,
                    medication_fee: `¥${prescription.prescription_package?.medication_fee.toLocaleString()}`,
                    shipping_fee: `¥${prescription.prescription_package?.shipping_fee.toLocaleString()}`,
                    total_price: `¥${prescription.prescription_package?.total_price.toLocaleString()}`,

                    status: prescription.status,
                    prescriptionNumber: prescription.prescription_number,
                    pharmacyId: prescription.pharmacy_id,
                    userAddress: prescription.user_address
                };
            });
            setPrescriptionHistory(transformedHistory);
        } catch (error) {
            //console.error('Error fetching prescription history:', error);
            const errorMessage = error?.status === 404
                ? t('No prescription history found for this patient')
                : error?.response?.data?.message || t('Failed to load prescription history');
            showSnackbar(errorMessage, 'info');
            setPrescriptionHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const fetchPharmacies = async () => {
        setPharmaciesLoading(true);
        try {
            const response = await api.get('v1/admin/all-pharmacies');
            setPharmacies(response.data.data || []);
            if (response.data && response.data.length > 0) {
                setSelectedPharmacy(response.data[0].id);
            }
        } catch (error) {
            console.error('Error fetching pharmacies:', error);
            const errorMessage = error?.response?.status === 403
                ? t('You do not have permission to view pharmacies')
                : t('Failed to fetch pharmacies');
            showSnackbar(errorMessage, 'error');
            setPharmacies([]);
        } finally {
            setPharmaciesLoading(false);
        }
    };

    const fetchPatients = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/v1/clinic/${user.id}/appointments`);
            if (response && response.data && response.data.data) {
                const transformedData = response.data.data.map((appointment) => ({
                    id: appointment["user.id"],
                    name: appointment["user.name"] || t("Unknown Patient"),
                    patientId: `PAT${String(appointment["user.id"]).padStart(3, "0")}`,
                    email: appointment["user.email"] || t("No email"),
                    phone: appointment["user.phone_number"] || t("No contact"),
                    appointmentId: appointment.id,
                    address: appointment.clinic_checkin_data?.address || ''
                }));
                setPatients(transformedData);
            }
        } catch (error) {
            console.error("Error fetching patients:", error);
            const errorMessage = error?.response?.status === 403
                ? t('You do not have permission to view patient appointments')
                : t('Failed to load patient list');
            showSnackbar(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePatientSelection = (patient) => {
        setSelectedPatientId(patient.id);
        setSelectedPatientData({
            id: patient.id,
            name: patient.name,
            age: 'N/A',
            medicalId: patient.patientId,
            phone: patient.phone,
            email: patient.email,
            address: patient.address || t('Loading address...')
        });
        setShowPatientDialog(false);
        setShowSelectPatientMessage(false);
        setSearchTerm('');
    };

    const handlePackageSelection = (packageId) => {
        setSelectedPackage(packageId);
        const selectedPkg = packages.find(pkg => pkg.id === packageId);
        if (selectedPkg && packageId !== 'CUSTOM') {
            const doseOnly = selectedPkg.dosage?.split('×')[0]?.trim();
            const isFG = selectedPkg.dosage.includes('×');

            setMedications([{
                id: Date.now(),
                name: t('Wegovy'),
                dose: doseOnly,
                frequency: '',
                duration: isFG ? `3 ${t('months')}` : '',
                units: 1,
                isUnitDisabled: isFG,
                isDurationDisabled: isFG,
                isFGPackage: isFG
            }]);
        } else if (packageId === 'CUSTOM') {
            setMedications([{
                id: Date.now(),
                name: '',
                dose: '',
                frequency: '',
                duration: '',
                units: 1,
                isUnitDisabled: false,
                isDurationDisabled: false,
                isFGPackage: false
            }]);
        }
        // setValidationErrors({});
        setTouchedFields({});
    };
    const showSnackbar = (message, type = 'success') => {
        setSnackbar({
            open: true,
            message,
            type
        });
    };

    const closeSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false
        });
    };

    useEffect(() => {
        fetchPrescriptionHistory();
    }, [activeTab])

    useEffect(() => {
        if (!selectedPatientId && !showPatientDialog && !showSelectPatientMessage) {
            setShowPatientDialog(true);
            fetchPatients();
            return;
        }

        if (selectedPatientId) {
            fetchPrescriptionHistory();
            fetchPatientAddress();

            if (isNewPrescriptionMode) {
                fetchPharmacies();
            }
        }
    }, [selectedPatientId, isNewPrescriptionMode, showPatientDialog, showSelectPatientMessage]);

    useEffect(() => {
        if (showPatientDialog && patients.length === 0) {
            fetchPatients();
        }
    }, [showPatientDialog]);

    const generateAvatar = (name) => {
        const nameParts = name?.split(" ");
        if (nameParts?.length >= 2) {
            return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
        }
        return name?.substring(0, 2).toUpperCase();
    };

    // const addMedication = () => {
    //     setMedications([...medications, { id: Date.now(), name: '', dose: '', frequency: '', duration: '', units: 1 }]);
    // };

    // const removeMedication = (id) => {
    //     if (medications.length > 1) {
    //         showConfirmation(
    //             t('Remove Medication'),
    //             t('Are you sure you want to remove this medication from the prescription?'),
    //             () => {
    //                 setMedications(medications.filter(med => med.id !== id));
    //                 const newErrors = { ...validationErrors };
    //                 delete newErrors[id];
    //                 setValidationErrors(newErrors);
    //                 const newTouchedFields = { ...touchedFields };
    //                 delete newTouchedFields[id];
    //                 setTouchedFields(newTouchedFields);
    //             }
    //         );
    //     }
    // };

    // const updateMedication = (id, field, value) => {
    //     setMedications(medications.map(med =>
    //         med.id === id ? { ...med, [field]: value } : med
    //     ));
    //     markFieldAsTouched(id, field);
    // };

    const handleSendToPrescription = async () => {
        // if (!validateAllMedications()) {
        //     showSnackbar(t('Please fix all validation errors before submitting'), 'error');
        //     return;
        // }

        if (!selectedPharmacy) {
            showSnackbar(t('Please select a pharmacy'), 'error');
            return;
        }

        if (!selectedPackage) {
            showSnackbar(t('Please select a package'), 'error');
            return;
        }

        const medicationSummary = medications.map(med =>
            `• ${med.name} ${med.dose} - ${med.frequency} ${t('for')} ${med.duration} (${t('Units')}: ${med.units || 1})`
        ).join('\n');

        const confirmMessage = `${t('You are about to create a prescription with the following medications:')}\n\n${medicationSummary}\n\n${t('Package:')} ${selectedPackage}\n${t('Pharmacy:')} ${pharmacies.find(p => p.id === selectedPharmacy)?.pharmacy_name}\n\n${t('This action cannot be undone. Continue?')}`;

        showConfirmation(
            t('Confirm Prescription'),
            confirmMessage,
            async () => {
                await submitPrescription();
            }
        );
    };

    const submitPrescription = async () => {
        const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);
        const totalAmount = selectedPkg ? selectedPkg.total_price : 0;
        const medicinesData = medications.map(med => ({
            name: med.name,
            dosage: med.dose,
            frequency: med.frequency,
            duration: med.duration,
            units: med.units || 1
        }));

        const payload = {
            user_id: appointmentData.user_id,
            appointment_id: appointmentData.id,
            medicines: medicinesData,
            prescribed_by: user.id,
            status: "pending",
            prescription_date: new Date().toISOString().split('T')[0],
            userAddress: patientAddress,
            pharmacy_id: parseInt(selectedPharmacy),
            package_id: selectedPackage === 'CUSTOM' ? 8 : selectedPackage, // Use package id directly
            total_amount: totalAmount
        };

        setSubmitting(true);
        try {
            const response = await api.post('v1/clinic/upload/user-prescription', payload);
            showSnackbar(t('Prescription created successfully!'), 'success');
            fetchPrescriptionHistory();
            setMedications([{ id: Date.now(), name: '', dose: '', frequency: '', duration: '', units: 1 }]);
            setSelectedPackage('');
            // setValidationErrors({});
            setDrugInteractions([]);
            setTouchedFields({});
        } catch (error) {
            console.error('Error sending prescription:', error);
            let errorMessage = t("Failed to create prescription");
            if (error?.response?.status === 400) {
                errorMessage = t("Invalid prescription data. Please check all fields.");
            } else if (error?.response?.status === 403) {
                errorMessage = t("You do not have permission to create prescriptions.");
            } else if (error?.response?.status === 404) {
                errorMessage = t("Patient or pharmacy not found.");
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            showSnackbar(errorMessage, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredHistory = prescriptionHistory.filter(prescription =>
        prescription.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prescription.patientId?.toString().includes(searchTerm.toLowerCase()) ||
        prescription.prescriptionNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredPatients = patients.filter(patient =>
        patient.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.patientId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading && !showPatientDialog) {
        return (
            <Backdrop open={true} sx={{ color: '#fff', zIndex: 9999 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
                        {t('Loading prescription data...')}
                    </Typography>
                </Box>
            </Backdrop>
        );
    }

    const handleDialogClose = () => {
        setShowPatientDialog(false);
        setSearchTerm('');
        if (!selectedPatientId) {
            setShowSelectPatientMessage(true);
        }
    };

    const handleOpenPatientDialog = () => {
        setShowSelectPatientMessage(false);
        setShowPatientDialog(true);
        if (patients.length === 0) {
            fetchPatients();
        }
    };

    if (showSelectPatientMessage) {
        return (
            <Box sx={{ p: 3, maxWidth: "100%", backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 200px)' }}>
                    <Paper elevation={0} sx={{ p: 6, textAlign: 'center', maxWidth: 600, width: '100%', border: '1px solid #e9ecef', borderRadius: '16px', background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: 0, right: 0, width: '150px', height: '150px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(220, 53, 69, 0.05) 100%)', transform: 'translate(50px, -50px)', zIndex: 0 }} />
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Box sx={{ width: 80, height: 80, borderRadius: '20px', background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3, boxShadow: '0 8px 32px rgba(220, 53, 69, 0.3)', position: 'relative', '&::before': { content: '""', position: 'absolute', inset: 2, borderRadius: '18px', background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)', zIndex: -1 } }}>
                                <PersonIcon sx={{ fontSize: 40, color: 'white' }} />
                            </Box>
                            <Typography variant="h5" fontWeight="700" sx={{ mb: 2, color: '#212529', lineHeight: 1.3 }}>
                                {patients.length === 0 ? t('No Patients Available') : t('Select Patient to Continue')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6, maxWidth: '400px', mx: 'auto' }}>
                                {patients.length === 0 ? t("No patients found in your appointments. This might be because you haven't had any recent appointments or there's a temporary issue loading the data.") : t('Please select a patient from your appointment list to view their prescription history or create new prescriptions.')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                {patients.length > 0 && (
                                    <Button variant="contained" onClick={handleOpenPatientDialog} startIcon={<PersonIcon />} sx={{ background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', px: 4, py: 1.5, fontWeight: 600, textTransform: 'none', borderRadius: '10px', boxShadow: '0 4px 16px rgba(220, 53, 69, 0.3)', fontSize: '16px', '&:hover': { background: 'linear-gradient(135deg, #c82333 0%, #bd2130 100%)', boxShadow: '0 6px 24px rgba(220, 53, 69, 0.4)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
                                        {t('Select Patient')}
                                    </Button>
                                )}
                                <Button variant="outlined" onClick={() => navigate(-1)} sx={{ color: '#6c757d', borderColor: '#dee2e6', px: 4, py: 1.5, fontWeight: 600, textTransform: 'none', borderRadius: '10px', fontSize: '16px', '&:hover': { borderColor: '#dc3545', color: '#dc3545', backgroundColor: 'rgba(220, 53, 69, 0.05)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
                                    {t('Go Back')}
                                </Button>
                            </Box>
                            {patients.length === 0 && (
                                <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #e9ecef' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {t('Need help? Try these options:')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <Button variant="text" size="small" onClick={fetchPatients} sx={{ color: '#dc3545', textTransform: 'none', '&:hover': { backgroundColor: 'rgba(220, 53, 69, 0.05)' } }}>
                                            {t('Refresh List')}
                                        </Button>
                                        <Button variant="text" size="small" onClick={() => { }} sx={{ color: '#6c757d', textTransform: 'none', '&:hover': { backgroundColor: 'rgba(108, 117, 125, 0.05)' } }}>
                                            {t('View Appointments')}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Box>
                {loading && (
                    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(248, 249, 250, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                        <Paper elevation={2} sx={{ p: 4, borderRadius: '12px', textAlign: 'center' }}>
                            <CircularProgress sx={{ color: '#dc3545', mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('Loading patients...')}
                            </Typography>
                        </Paper>
                    </Box>
                )}
            </Box>
        );
    }

    const prescriptionCardToCsvRow = (prescription) => {
        const meds = (prescription.medications || []).map(m => {
            const dose = m.dose || m.dosage || '';
            const desc = m.description || '';
            return `${m.name || ''}${dose ? ' - ' + dose : ''}${desc ? ' (' + desc + ')' : ''}`;
        }).join('; ');

        return {
            patient_name: prescription.patientName || '',
            patient_id: prescription.patientId || '',
            date: prescription.date || '',
            time: prescription.time || '',
            medication_count: prescription.medicationCount || 0,
            medications: meds,
            package: prescription.package || '',
            // price: prescription.price || '',

            consultation_fee: prescription.consultation_fee || '',
            medication_fee: prescription.medication_fee || '',
            shipping_fee: prescription.shipping_fee || '',
            total_price: prescription.total_price || '',

            prescription_number: prescription.prescriptionNumber || '',
            pharmacy_id: prescription.pharmacyId || '',
            user_address: prescription.userAddress || ''
        };
    };

    const prescriptionCardCsvColumns = [
        { id: 'patient_name', label: t('Patient Name') },
        { id: 'patient_id', label: t('Patient ID') },
        { id: 'date', label: t('Date') },
        { id: 'time', label: t('Time') },
        { id: 'medication_count', label: t('Medication Count') },
        { id: 'medications', label: t('Medications') },
        { id: 'package', label: t('Package') },

        { id: 'consultation_fee', label: t('Consultation Fee') },
        { id: 'medication_fee', label: t('Medication Fee') },
        { id: 'shipping_fee', label: t('Shipping Fee') },
        { id: 'total_price', label: t('Total Price') },

        { id: 'prescription_number', label: t('Prescription Number') },
        { id: 'pharmacy_id', label: t('Pharmacy ID') },
        { id: 'user_address', label: t('User Address') }
    ];

    const handleDownloadVisiblePrescriptions = () => {
        if (!filteredHistory || filteredHistory.length === 0) {
            showSnackbar(t('No prescriptions to download'), 'info');
            return;
        }
        const rows = filteredHistory.map(prescriptionCardToCsvRow);

        const fileBase = `${t('Prescription_History')}_${new Date().toISOString().slice(0, 10)}`;
        DownloadCSV(prescriptionCardCsvColumns, rows, fileBase);
    };

    return (
        <>
            <Box sx={{ p: 3, maxWidth: "100%", backgroundColor: "background.default", minHeight: "100vh" }}>
                {drugInteractions.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 3, border: '1px solid #ff9800' }}>
                        <AlertTitle>{t('Potential Drug Interactions Detected')}</AlertTitle>
                        {drugInteractions.map((interaction, index) => (
                            <Typography key={index} variant="body2" sx={{ mt: 1 }}>
                                <strong>{interaction.drugs.join(' + ')}:</strong> {interaction.warning}
                            </Typography>
                        ))}
                        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                            {t('Please review these interactions before submitting the prescription.')}
                        </Typography>
                    </Alert>
                )}

                {isNewPrescriptionMode && selectedPatientId && (
                    <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', backgroundColor: '#f8f9fa' }}>
                            <Button onClick={() => setActiveTab('new')} startIcon={<AddIcon />} sx={{ flex: 1, borderRadius: 0, backgroundColor: activeTab === 'new' ? 'white' : 'transparent', color: activeTab === 'new' ? '#dc3545' : 'text.secondary', fontWeight: activeTab === 'new' ? 700 : 500, px: 4, py: 2, textTransform: 'none', fontSize: '15px', borderBottom: activeTab === 'new' ? '3px solid #dc3545' : '3px solid transparent', '&:hover': { backgroundColor: activeTab === 'new' ? 'white' : 'rgba(220, 53, 69, 0.05)', color: '#dc3545' } }}>
                                {t('New Prescription')}
                            </Button>
                            <Button onClick={() => setActiveTab('history')} startIcon={<TimeIcon />} sx={{ flex: 1, borderRadius: 0, backgroundColor: activeTab === 'history' ? 'white' : 'transparent', color: activeTab === 'history' ? '#dc3545' : 'text.secondary', fontWeight: activeTab === 'history' ? 700 : 500, px: 4, py: 2, textTransform: 'none', fontSize: '15px', borderBottom: activeTab === 'history' ? '3px solid #dc3545' : '3px solid transparent', '&:hover': { backgroundColor: activeTab === 'history' ? 'white' : 'rgba(220, 53, 69, 0.05)', color: '#dc3545' } }}>
                                {t('Prescription History')}
                            </Button>
                        </Box>
                    </Paper>
                )}

                {selectedPatientId && isNewPrescriptionMode && activeTab === 'new' && (
                    <Box>
                        <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: '#f8f9fa' }}>
                                <Typography variant="h6" fontWeight="600">{t('Patient Information')}</Typography>
                            </Box>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                                    <Avatar sx={{ width: 60, height: 60, backgroundColor: '#dc3545', fontSize: '20px', fontWeight: 600 }}>
                                        {generateAvatar(selectedPatient.name)}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" fontWeight="600">{selectedPatient.name}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {t('Age:')} {selectedPatient.age} • {t('Medical ID:')} {selectedPatient.medicalId}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PhoneIcon fontSize="small" />
                                                <Typography variant="body2">{selectedPatient.phone}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <EmailIcon fontSize="small" />
                                                <Typography variant="body2">{selectedPatient.email}</Typography>
                                            </Box>
                                        </Box>
                                        {appointmentData && (
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
                                                {selectedPatient.bmi !== 'N/A' && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">{t('BMI')}</Typography>
                                                        <Typography variant="body2" fontWeight="600">{selectedPatient.bmi}</Typography>
                                                    </Box>
                                                )}
                                                {selectedPatient.height !== 'N/A' && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">{t('Height (cm)')}</Typography>
                                                        <Typography variant="body2" fontWeight="600">{selectedPatient.height}</Typography>
                                                    </Box>
                                                )}
                                                {selectedPatient.weight !== 'N/A' && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">{t('Weight (kg)')}</Typography>
                                                        <Typography variant="body2" fontWeight="600">{selectedPatient.weight}</Typography>
                                                    </Box>
                                                )}
                                                {selectedPatient.sex !== 'N/A' && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">{t('Gender')}</Typography>
                                                        <Typography variant="body2" fontWeight="600">{selectedPatient.sex}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                                            <LocationIcon fontSize="small" sx={{ mt: 0.2 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">{t('Address')}</Typography>
                                                {patientLoading ? (
                                                    <CircularProgress size={16} sx={{ ml: 1 }} />
                                                ) : (
                                                    <Typography variant="body2">{selectedPatient.address}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        {selectedPatient.symptoms && selectedPatient.symptoms !== t('No symptoms recorded') && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">{t('Reported Symptoms')}</Typography>
                                                <Typography variant="body2" sx={{ backgroundColor: '#f8f9fa', p: 2, borderRadius: '8px', mt: 1 }}>
                                                    {selectedPatient.symptoms}
                                                </Typography>
                                            </Box>
                                        )}
                                        {appointmentData?.notes?.trim() && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="caption" color="text.secondary">{t('Consultation Notes')}</Typography>
                                                <Typography variant="body2" sx={{ backgroundColor: '#f8f9fa', p: 2, borderRadius: '8px', mt: 1 }}>
                                                    {appointmentData.notes}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Paper>

                        <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: '#f8f9fa' }}>
                                <Typography variant="h6" fontWeight="600">{t('Select Package')}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {t('Choose the treatment plan that best suits your needs')}
                                </Typography>
                            </Box>
                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, '@media (min-width: 1400px)': { gridTemplateColumns: 'repeat(7, 1fr)' }, '@media (min-width: 1200px) and (max-width: 1399px)': { gridTemplateColumns: 'repeat(4, 1fr)' }, '@media (min-width: 900px) and (max-width: 1199px)': { gridTemplateColumns: 'repeat(3, 1fr)' }, '@media (min-width: 600px) and (max-width: 899px)': { gridTemplateColumns: 'repeat(2, 1fr)' }, '@media (max-width: 599px)': { gridTemplateColumns: '1fr' } }}>
                                    {packages.map((pkg) => (
                                        <Card key={pkg.id} sx={{ cursor: 'pointer', border: selectedPackage === pkg.id ? '2px solid #dc3545' : '2px solid #e0e0e0', boxShadow: selectedPackage === pkg.id ? '0 4px 16px rgba(220, 53, 69, 0.2)' : 'none', textAlign: 'center', transition: 'all 0.25s ease', backgroundColor: selectedPackage === pkg.id ? '#fff5f5' : 'white', '&:hover': { boxShadow: '0 6px 20px rgba(220, 53, 69, 0.25)', transform: 'translateY(-2px)', borderColor: '#dc3545' }, opacity: submitting ? 0.6 : 1, pointerEvents: submitting ? 'none' : 'auto', position: 'relative', overflow: 'hidden', borderRadius: '12px' }} onClick={() => !submitting && handlePackageSelection(pkg.id)}>
                                            {selectedPackage === pkg.id && (
                                                <Box sx={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, borderStyle: 'solid', borderWidth: '0 45px 45px 0', borderColor: 'transparent #dc3545 transparent transparent' }}>
                                                    <CheckIcon sx={{ position: 'absolute', top: 5, right: -40, fontSize: 16, color: 'white' }} />
                                                </Box>
                                            )}
                                            <CardContent>
                                                <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: selectedPackage === pkg.id ? '#dc3545' : '#fee', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', transition: 'all 0.25s ease' }}>
                                                    <Typography variant="h4" sx={{ color: selectedPackage === pkg.id ? 'white' : '#dc3545', fontWeight: 600, fontSize: '1.3rem' }}>
                                                        {pkg.package_name.split(' ')[1]}
                                                    </Typography>
                                                </Box>
                                                
                                                {/* <Typography variant="subtitle1" sx={{ color: '#dc3545', fontWeight: 700, fontSize: '1.5rem', mb: 1, lineHeight: 1.2 }}>
                                                    {pkg.package_name}
                                                </Typography> */}

                                                {(() => {
                                                    const [mgPart, monthPart] = pkg.dosage.split('×');
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                backgroundColor: selectedPackage === pkg.id ? '#dc3545' : '#F3F4F6',
                                                                borderRadius: '16px',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                mb: 1,
                                                                minWidth: '80px',
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    color: selectedPackage === pkg.id ? 'white' : '#121212',
                                                                    fontWeight: 600,
                                                                    fontSize: '1.3rem',
                                                                    lineHeight: 1.5,
                                                                }}
                                                            >
                                                                {mgPart?.trim()}
                                                            </Typography>
                                                            {monthPart && (
                                                                <Typography
                                                                    sx={{
                                                                        color: selectedPackage === pkg.id ? 'rgba(255,255,255,0.85)' : '#6B7280',
                                                                        fontWeight: 500,
                                                                        fontSize: '0.8rem',
                                                                        lineHeight: 1.2,
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    ×{monthPart.trim()}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    );
                                                })()}
                                                <Typography variant="h6" fontWeight="700" sx={{ color: '#4B5563', fontSize: '1rem' }}>
                                                    ¥{pkg.total_price.toLocaleString()}
                                                </Typography>
                                                {/* <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                    {t('Total package cost')}
                                                </Typography> */}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            </CardContent>
                        </Paper>

                        <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: '#f8f9fa' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" fontWeight="600">{t('Prescription')}</Typography>

                                    {/* <Button variant="contained" startIcon={<AddIcon />} onClick={addMedication} size="small" disabled={submitting} sx={{ backgroundColor: '#dc3545' }}>
                                        {t('Add Medicine')}
                                    </Button> */}
                                </Box>
                            </Box>
                            <CardContent>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>{t('Medicine name')}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{t('Dose')}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{t('Frequency')}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{t('Duration')}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{t('Units')}</TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody sx={{
                                            '& .MuiTableRow-root': {
                                                height: 70
                                            }
                                        }}>
                                            {medications.map((medication) => (
                                                <TableRow key={medication.id}>
                                                    <TableCell sx={{ width: '18%' }}>
                                                        <Autocomplete
                                                            freeSolo
                                                            size="small"
                                                            fullWidth
                                                            options={medicineOptions}
                                                            value={medication.name}
                                                            onChange={(event, newValue) => { updateMedication(medication.id, 'name', newValue || ''); }}
                                                            onInputChange={(event, newInputValue) => { updateMedication(medication.id, 'name', newInputValue); }}
                                                            disabled={submitting || (selectedPackage && selectedPackage !== 'CUSTOM')}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder={t("Select or type medicine name")}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    error={!!validationErrors[medication.id]?.name}
                                                                    helperText={validationErrors[medication.id]?.name}
                                                                    FormHelperTextProps={{ sx: { fontSize: '12px', mt: 0.5 } }}
                                                                    disabled={submitting || (selectedPackage && selectedPackage !== 'CUSTOM')}
                                                                />
                                                            )}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ width: '20%' }}>
                                                        {/* <Tooltip title={t('Format: number + unit (e.g., 5mg, 2.5ml, 10units)')} arrow
                                                            PopperProps={{ disablePortal: true }}> */}
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            placeholder={t('e.g., 5mg')}
                                                            value={medication.dose}
                                                            onChange={(e) => updateMedication(medication.id, 'dose', e.target.value)}
                                                            error={!!validationErrors[medication.id]?.dose}
                                                            helperText={validationErrors[medication.id]?.dose}
                                                            FormHelperTextProps={{ sx: { fontSize: '12px', mt: 0.5 } }}
                                                            disabled={submitting || (selectedPackage && selectedPackage !== 'CUSTOM')}
                                                            sx={{ width: '100%' }}
                                                        />
                                                        {/* </Tooltip> */}
                                                    </TableCell>
                                                    <TableCell sx={{ width: '21%' }}>
                                                        <Autocomplete
                                                            size="small"
                                                            fullWidth
                                                            options={frequencyOptions}
                                                            value={medication.frequency || ''}
                                                            onChange={(event, newValue) => {
                                                                updateMedication(
                                                                    medication.id,
                                                                    'frequency',
                                                                    newValue || ''
                                                                );
                                                            }}
                                                            disabled={submitting}
                                                            clearOnEscape
                                                            forcePopupIcon={false}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder={t('Select frequency')}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            )}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ width: '21%' }}>
                                                        <Autocomplete
                                                            freeSolo
                                                            size="small"
                                                            fullWidth
                                                            options={getDurationOptions(medication.frequency, medication.isFGPackage)}
                                                            value={medication.duration || ''}
                                                            onChange={(event, newValue) => { updateMedication(medication.id, 'duration', newValue || ''); }}
                                                            onInputChange={(event, newInputValue) => { updateMedication(medication.id, 'duration', newInputValue); }}
                                                            disabled={submitting || medication.isFGPackage}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder={t('Select duration')}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    // error={!!validationErrors[medication.id]?.duration}
                                                                    // helperText={validationErrors[medication.id]?.duration}
                                                                    // FormHelperTextProps={{ sx: { fontSize: '12px', mt: 0.5 } }}
                                                                    disabled={submitting || medication.isFGPackage}
                                                                />
                                                            )}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    </TableCell>

                                                    <TableCell sx={{ width: '10%' }}>
                                                        <Tooltip
                                                            title={
                                                                medication.isUnitDisabled
                                                                    ? t('Unit is fixed for this package')
                                                                    : ''
                                                            }
                                                            arrow
                                                            PopperProps={{ disablePortal: true }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                                                                <Autocomplete
                                                                    freeSolo
                                                                    size="small"
                                                                    fullWidth
                                                                    placeholder={t('Units')}
                                                                    disabled={medication.isUnitDisabled}
                                                                    options={getUnitsOptions(medication.frequency).map(String)}
                                                                    value={medication.units !== '' && medication.units != null ? String(medication.units) : ''}
                                                                    onChange={(event, newValue) => {
                                                                        if (newValue === null || newValue === '') {
                                                                            updateMedication(medication.id, 'units', '');
                                                                            return;
                                                                        }
                                                                        const num = parseInt(newValue, 10);
                                                                        if (!Number.isNaN(num) && num > 0) {
                                                                            updateMedication(medication.id, 'units', num);
                                                                        }
                                                                    }}
                                                                    onInputChange={(event, newInputValue, reason) => {
                                                                        if (reason === 'reset') return;
                                                                        if (newInputValue === '') {
                                                                            updateMedication(medication.id, 'units', '');
                                                                            return;
                                                                        }
                                                                        if (!/^[0-9]+$/.test(newInputValue)) return;
                                                                        const num = parseInt(newInputValue, 10);
                                                                        if (num <= 0) return;
                                                                        updateMedication(medication.id, 'units', num);
                                                                    }}
                                                                    onBlur={() => {
                                                                        const step = getUnitsStep(medication.frequency);
                                                                        const current = parseInt(medication.units, 10);
                                                                        if (!current || current <= 0) {
                                                                            updateMedication(medication.id, 'units', step);
                                                                            return;
                                                                        }
                                                                        if (medication.frequency && current % step !== 0) {
                                                                            const snapped = Math.ceil(current / step) * step;
                                                                            updateMedication(medication.id, 'units', snapped);
                                                                        }
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            size="small"
                                                                            onFocus={(e) => e.target.select()}
                                                                            onKeyDown={(e) => {
                                                                                if (['-', '+', '.', ',', 'e', 'E'].includes(e.key)) {
                                                                                    e.preventDefault();
                                                                                }
                                                                            }}
                                                                            onPaste={(e) => {
                                                                                const pasted = e.clipboardData.getData('text');
                                                                                if (!/^[0-9]+$/.test(pasted) || parseInt(pasted, 10) <= 0) {
                                                                                    e.preventDefault();
                                                                                }
                                                                            }}
                                                                            inputProps={{
                                                                                ...params.inputProps,
                                                                                inputMode: 'numeric',
                                                                                pattern: '[0-9]*',
                                                                            }}
                                                                        />
                                                                    )}
                                                                    sx={{ width: 100 }}
                                                                />

                                                            </Box>
                                                        </Tooltip>
                                                    </TableCell>
                                                    {/* {selectedPackage && (
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Tooltip title={t('Add medication')} arrow>
                                                                    <IconButton size="small" onClick={addMedication} sx={{ color: '#4caf50' }} disabled={submitting}>
                                                                        <AddIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {medications.length > 1 && (
                                                                    <Tooltip title={t('Remove medication')} arrow>
                                                                        <IconButton size="small" onClick={() => removeMedication(medication.id)} sx={{ color: '#d32f2f' }} disabled={submitting}>
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    )} */}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Paper>

                        <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: '#f8f9fa' }}>
                                <Typography variant="h6" fontWeight="600">{t('Select Pharmacy*')}</Typography>
                            </Box>
                            <CardContent>
                                {pharmaciesLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                                        <CircularProgress size={24} sx={{ mr: 2 }} />
                                        <Typography variant="body2" color="text.secondary">{t('Loading pharmacies...')}</Typography>
                                    </Box>
                                ) : pharmacies.length === 0 ? (
                                    <Alert severity="error" sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2">{t('No pharmacies available')}</Typography>
                                        <Button variant="outlined" size="small" onClick={fetchPharmacies} sx={{ mt: 1, color: '#dc3545', borderColor: '#dc3545' }}>
                                            {t('Retry Loading')}
                                        </Button>
                                    </Alert>
                                ) : (
                                    <Grid container spacing={2}>
                                        {pharmacies.map((pharmacy) => (pharmacy?.status &&
                                            <Grid item xs={12} sm={6} md={4} key={pharmacy.id}>
                                                <Card sx={{ border: selectedPharmacy === pharmacy.id ? '2px solid #dc3545' : '1px solid #e0e0e0', cursor: 'pointer', '&:hover': { boxShadow: 2 }, opacity: submitting ? 0.6 : 1, pointerEvents: submitting ? 'none' : 'auto', height: '100%' }} onClick={() => !submitting && setSelectedPharmacy(pharmacy.id)}>
                                                    <CardContent sx={{ p: 2 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                            <Typography variant="subtitle1" fontWeight="600" sx={{ fontSize: '0.9rem', lineHeight: 1.2 }}>{pharmacy.pharmacy_name}</Typography>
                                                            <Chip label={t('OPEN')} size="small" color="success" sx={{ fontSize: '0.65rem', height: '20px' }} />
                                                        </Box>
                                                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                                            {[pharmacy.address, pharmacy.city, pharmacy.postal_code].filter(Boolean).join(', ')}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>{pharmacy.phone_number}</Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{pharmacy.email}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </CardContent>
                        </Paper>

                        {submitting && (
                            <Paper elevation={0} sx={{ mb: 3, border: '1px solid #2196f3', backgroundColor: '#e3f2fd', borderRadius: '12px' }}>
                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CircularProgress size={24} />
                                    <Box>
                                        <Typography variant="body2" fontWeight="600" color="primary">
                                            {t('Creating Prescription...')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('Please wait while we process your prescription')}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Paper>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button variant="outlined" sx={{ px: 4 }} disabled={submitting} onClick={() => navigate(-1)}>
                                {t('Cancel')}
                            </Button>
                            <Button variant="contained" startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />} onClick={handleSendToPrescription} disabled={submitting || pharmaciesLoading || !selectedPackage} sx={{ backgroundColor: "#dc3545", px: 4 }}>
                                {submitting ? t('Creating...') : t('Create Prescription')}
                            </Button>
                        </Box>
                    </Box>
                )}

                {selectedPatientId && (activeTab === 'history' || isHistoryOnlyMode) && (
                    <Box>
                        {selectedPatientId && (
                            <Paper elevation={0} sx={{ mb: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: '#f8f9fa' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ width: 40, height: 40, backgroundColor: '#dc3545', fontSize: '14px', fontWeight: 600 }}>
                                        {generateAvatar(selectedPatient.name)}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" fontWeight="600">{selectedPatient.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">{selectedPatient.medicalId}</Typography>
                                    </Box>
                                    <Button variant="outlined" size="small" onClick={() => setShowPatientDialog(true)} sx={{ color: '#dc3545', borderColor: '#dc3545' }} disabled={loading}>
                                        {t('Change Patient')}
                                    </Button>
                                </Box>
                            </Paper>
                        )}
                        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                            <TextField placeholder={t('Search prescriptions...')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ width: "300px" }} />
                            {/* <Select value="All Time" size="small" sx={{ width: 150 }}>
                                <MenuItem value="All Time">{t('All Time')}</MenuItem>
                                <MenuItem value="Last 7 days">{t('Last 7 days')}</MenuItem>
                                <MenuItem value="Last 30 days">{t('Last 30 days')}</MenuItem>
                            </Select> */}
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadVisiblePrescriptions}
                                disabled={historyLoading || filteredHistory.length === 0}
                                sx={{ ml: 'auto' }}
                            >
                                {t('Download Prescriptions')}
                            </Button>

                        </Box>
                        {historyLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                                <CircularProgress sx={{ mr: 2 }} />
                                <Typography variant="body2" color="text.secondary">{t('Loading prescription history...')}</Typography>
                            </Box>
                        ) : filteredHistory.length === 0 ? (
                            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                <InfoIcon sx={{ fontSize: 48, color: '#6c757d', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('No prescription history found')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {searchTerm ? t('Try adjusting your search terms.') : t('This patient has no previous prescriptions.')}
                                </Typography>
                            </Paper>
                        ) : (
                            filteredHistory.map((prescription) => (
                                <Paper key={prescription.id} elevation={0} sx={{ mb: 3, border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ width: 40, height: 40, backgroundColor: '#dc3545', fontSize: '14px', fontWeight: 600 }}>
                                                    {generateAvatar(prescription.patientName)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="h6" fontWeight="600">{prescription.patientName}</Typography>
                                                    <Typography variant="body2" color="text.secondary">{t('ID:')} {prescription.patientId}</Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="body2" color="text.secondary">{prescription.date}</Typography>
                                                <Typography variant="body2" color="text.secondary">{prescription.time}</Typography>
                                                {/* <Chip label={`${prescription.medicationCount} ${t('Medications')}`} size="small" sx={{ backgroundColor: 'rgba(25, 118, 210, 0.1)', color: '#1976d2', fontWeight: 600, mt: 1 }} /> */}
                                            </Box>
                                        </Box>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2 }}>{t('Medications:')}</Typography>
                                            {prescription.medications?.map((med, index) => (
                                                <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ color: '#dc3545', fontWeight: 600 }}>
                                                            {med.name} - {med.dose}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">{med.description}</Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                        {t('Quantity:')} {med.units ?? 1}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: '1px solid #e9ecef' }}>
                                            <Typography variant="body2" fontWeight="600">
                                                {t(prescription.package)} - {prescription.total_price}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Paper>
                            ))
                        )}
                    </Box>
                )}

                <CustomSnackbar message={snackbar.message} isOpen={snackbar.open} onClose={closeSnackbar} type={snackbar.type} sx={{ zIndex: 1400 }} />
            </Box>

            <Dialog open={confirmDialog.open} onClose={closeConfirmation} maxWidth="sm" fullWidth translate="no" >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ color: '#ff9800' }} />
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body1"
                        translate="no"
                        sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}
                    >
                        {confirmDialog.message || ''}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button onClick={closeConfirmation} variant="outlined" disabled={submitting}>
                        {t('Cancel')}
                    </Button>
                    <Button onClick={() => { confirmDialog.onConfirm && confirmDialog.onConfirm(); closeConfirmation(); }} variant="contained" sx={{ backgroundColor: '#dc3545' }} disabled={submitting} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}>

                        {submitting ? t('Processing...') : t('Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showPatientDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">{t('Select Patient')}</Typography>
                        <IconButton onClick={handleDialogClose} disabled={loading}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3, mt: 2 }}>
                        <TextField placeholder={t('Search patients...')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" fullWidth InputProps={{ startAdornment: (<InputAdornment position="start"> <SearchIcon /> </InputAdornment>) }} disabled={loading} />
                    </Box>
                    {loading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <LinearProgress sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">{t('Loading patients...')}</Typography>
                        </Box>
                    ) : patients.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{t('No patients found')}</Typography>
                            <Button variant="contained" onClick={fetchPatients} sx={{ backgroundColor: '#dc3545' }}>
                                {t('Retry')}
                            </Button>
                        </Box>
                    ) : (
                        <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('Name')}</TableCell>
                                        <TableCell>{t('Patient ID')}</TableCell>
                                        <TableCell>{t('Email')}</TableCell>
                                        <TableCell>{t('Phone')}</TableCell>
                                        <TableCell>{t('Action')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredPatients.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('No patients match your search criteria')}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredPatients.map((patient) => (
                                            <TableRow key={patient.id}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar sx={{ width: 32, height: 32, backgroundColor: '#dc3545', fontSize: '12px' }}>
                                                            {generateAvatar(patient.name)}
                                                        </Avatar>
                                                        <Typography variant="body2">{patient.name}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {patient.patientId}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{patient.email}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{patient.phone}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Button variant="contained" size="small" onClick={() => handlePatientSelection(patient)} sx={{ backgroundColor: '#dc3545' }}>
                                                        {t('Select')}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} disabled={loading}>{t('Cancel')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PrescriptionManagement;
